/**
 * Everything the module editor reads, writes and holds while it is open.
 *
 * The write path is the module workspace's: a per-module edit lease, a save
 * composed against the record this editor read, and the conflict panel as the
 * failure path. The draft, its window and its save are the edit session's
 * module target, so a draft whose save could not land outlives this editor.
 *
 * @module components/studio/useModuleEditor
 */

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";

import { moduleDraftEdits } from "@/components/studio/module-draft-edits";
import { asError, returnedToBuilder, serialized } from "@/components/studio/module-editor-model";
import { useModuleLease } from "@/components/studio/module-lease-renewal";
import { useStoredCopy } from "@/components/studio/module-stored-copy";
import { useAssessmentLedger } from "@/components/studio/useAssessmentLedger";
import { useViewOnlyRecord } from "@/components/studio/view-only-record";
import type { ModuleWorkspaceRecord, ProgrammePlatformClient } from "@/platform";
import { moduleTargetKey, useEditSession } from "@/platform/editor-sync-context";
import type { ModuleSaveStatus } from "@/platform/module-edit-target";
import type { Module, Programme } from "@/types";
import { uid } from "@/utils/uid";

export type SaveStatus = ModuleSaveStatus;
type ReviewDecision =
  "request-changes" | "request-suggestions" | "not-approve" | "approve" | "reopen";
export type ReviewAction = "submit" | ReviewDecision;

/** The same tier the programme store autosaves on. */

export interface UseModuleEditorOptions {
  client: ProgrammePlatformClient;
  programmeId: string;
  moduleId: string;
  /** Edit this change case's working copy rather than the live programme. */
  changeCaseId?: string;
  onSaved?: () => Promise<void> | void;
  editLeasesEnabled?: boolean;
  clientId?: string;
  /** Who is reading, so a module returned to its builder is left to them. */
  readerUserId?: string;
  /** The programme the module sits in, or the change case's working copy of it. */
  programme?: Pick<Programme, "modules" | "versions">;
  /** Opened to view: no lease, no edits, no review decisions. */
  viewOnly?: boolean;
}

export function useModuleEditor({
  client,
  programmeId,
  moduleId,
  changeCaseId = undefined,
  onSaved = undefined,
  editLeasesEnabled = true,
  clientId: sharedClientId = undefined,
  readerUserId = undefined,
  programme = undefined,
  viewOnly = false,
}: UseModuleEditorOptions) {
  const session = useEditSession();
  // The editor is keyed on its module, so one mount asks for one target.
  const [target] = useState(() =>
    session.moduleTarget(moduleTargetKey(programmeId, changeCaseId, moduleId)),
  );
  const {
    record: readRecord,
    module: moduleDraft,
    ploToMimlos,
    baseline,
    status: saveStatus,
    error: saveError,
    conflict: moduleConflict,
  } = useSyncExternalStore(target.subscribe, target.getState);
  const record = useViewOnlyRecord(readRecord, viewOnly);
  const [loadError, setLoadError] = useState<Error | null>(null);
  // Bumped to read the module again after a failed first read.
  const [loadAttempt, setLoadAttempt] = useState(0);
  // Loading until the read answers either way; a retry clears the error.
  const loading = !record && !loadError;
  const [reviewAction, setReviewAction] = useState<ReviewAction | null>(null);
  const [reviewActionError, setReviewActionError] = useState<Error | null>(null);
  const [reviewNote, setReviewNote] = useState("");
  // A change case writes its own copy, so the live module's lease is not its
  // to take, and the server refuses it on a locked programme.
  const needsLease = editLeasesEnabled && !changeCaseId && !viewOnly;
  const { behindStoredCopy, noteAdopted } = useStoredCopy(
    client,
    programmeId,
    moduleId,
    changeCaseId,
    record,
  );
  // State rather than a ref, so the id is read during render without breaking
  // the hook rules. Only a caller with no shell above it gets its own.
  const [ownClientId] = useState(() => uid("module-editor"));
  const clientId = sharedClientId ?? ownClientId;
  const {
    lease: editLease,
    leaseRef: editLeaseRef,
    leaseError,
    hold: holdLease,
    release: releaseLease,
    acquire: retryLease,
    acquiring: leaseAcquiring,
    setLeaseError,
    handedBack,
    handBack,
  } = useModuleLease(client, clientId, editLeasesEnabled, programmeId, moduleId);

  /**
   * Adopt a freshly read record: the draft and its baseline are reseeded from
   * it together, so a reload and a review decision leave the editor clean
   * against exactly what was read.
   */
  const adoptRecord = useCallback(
    (next: ModuleWorkspaceRecord) => {
      noteAdopted();
      return target.adopt(next);
    },
    [noteAdopted, target],
  );

  // No reset on the way in: the editor is keyed on the module it edits, so a
  // different module arrives as a fresh mount, and the cleanup below releases
  // the lease the outgoing one held.
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const nextRecord = await client.getModuleWorkspace(programmeId, moduleId, changeCaseId);
        if (!active) {
          return;
        }
        const returned = needsLease && returnedToBuilder(nextRecord, readerUserId);
        handBack(returned);
        if (nextRecord.canEdit && needsLease && !returned) {
          try {
            const lease = await client.acquireEditLease({ programmeId, moduleId, clientId });
            if (!active) {
              client.releaseEditLease({ leaseId: lease.id, clientId }).catch(() => undefined);
              return;
            }
            holdLease(lease);
          } catch (cause) {
            setLeaseError(asError(cause, "Unable to acquire the module editing lease"));
          }
        }
        // A draft this module's last editor left unsent comes back over the read.
        noteAdopted();
        target.reopen(nextRecord);
      } catch (cause) {
        if (active) {
          setLoadError(asError(cause, "Unable to load the module workspace"));
        }
      }
    })();

    return () => {
      active = false;
      const lease = editLeaseRef.current;
      editLeaseRef.current = null;
      // Released only once the pending edit has been sent under it.
      const saved = target.leave().catch(() => undefined);
      if (lease) {
        // Unmounting with the API unreachable must not raise unhandled.
        saved
          .then(() => client.releaseEditLease({ leaseId: lease.id, clientId }))
          .catch(() => undefined);
      }
    };
  }, [
    changeCaseId,
    client,
    clientId,
    editLeaseRef,
    handBack,
    holdLease,
    loadAttempt,
    moduleId,
    needsLease,
    noteAdopted,
    programmeId,
    readerUserId,
    setLeaseError,
    target,
  ]);

  const retryLoad = useCallback(() => {
    setLoadError(null);
    setLoadAttempt((current) => current + 1);
  }, []);

  // A reload replaces the draft when it lands, so what is typed while it is out
  // would be lost; the editor holds until it answers.
  const [reloading, setReloading] = useState(false);
  const draftKey = moduleDraft ? serialized(moduleDraft, ploToMimlos) : null;
  const dirty = draftKey !== null && draftKey !== baseline;
  const canEdit = Boolean(
    record?.canEdit && (!needsLease || editLease) && !leaseError && !reloading,
  );

  // Read when the target saves, so it writes under the lease this render holds.
  const latest = useRef({ canEdit, leaseId: editLease?.id, onSaved });
  useLayoutEffect(() => {
    latest.current = { canEdit, leaseId: editLease?.id, onSaved };
  });
  // Declared after the read, so leaving sends before the target is detached.
  useEffect(
    () =>
      target.attach({
        client,
        changeCaseId,
        editLeaseId: () => latest.current.leaseId,
        canEdit: () => latest.current.canEdit,
        onAdopted: noteAdopted,
        onSaved: () => latest.current.onSaved?.(),
      }),
    [changeCaseId, client, noteAdopted, target],
  );
  // A draft that waited for the lease or a reload goes once the editor can write.
  useEffect(() => {
    if (canEdit) {
      target.resume();
    }
  }, [canEdit, target]);

  const updateDraft = useCallback(
    (update: (module: Module) => void): void => {
      target.edit((draft) => {
        update(draft.module);
      });
    },
    [target],
  );

  const editMappings = useCallback(
    (update: (current: Record<string, string[]>) => Record<string, string[]>): void => {
      target.edit((draft) => {
        draft.ploToMimlos = update(draft.ploToMimlos);
      });
    },
    [target],
  );

  const reloadWorkspace = useCallback(async (): Promise<void> => {
    setReloading(true);
    try {
      const next = await client.getModuleWorkspace(programmeId, moduleId, changeCaseId);
      adoptRecord(next);
      // Opened read only, it took no lease on mount; `acquire` skips a held one.
      const returned = needsLease && !editLeaseRef.current && returnedToBuilder(next, readerUserId);
      handBack(returned);
      await (next.canEdit && needsLease && !returned ? retryLease() : undefined);
      target.setConflict(null);
      target.setStatus("idle");
      target.setError(null);
    } catch (cause) {
      target.setError(asError(cause, "Unable to reload the module"));
    } finally {
      setReloading(false);
    }
  }, [
    adoptRecord,
    changeCaseId,
    client,
    editLeaseRef,
    handBack,
    moduleId,
    needsLease,
    programmeId,
    readerUserId,
    retryLease,
    target,
  ]);

  const applyReviewRecord = useCallback(
    async (next: ModuleWorkspaceRecord): Promise<void> => {
      adoptRecord(next);
      // A decision returning a module read only on mount is what makes it
      // editable. A return hands it to its builder, so the lease stays free.
      const returned = needsLease && returnedToBuilder(next, readerUserId);
      handBack(returned);
      await (next.canEdit && needsLease && !returned ? retryLease() : undefined);
      await onSaved?.();
    },
    [adoptRecord, handBack, needsLease, onSaved, readerUserId, retryLease],
  );

  const submitForReview = useCallback(
    async (options: { note: string; warningsAcknowledged: boolean }): Promise<boolean> => {
      if (!record || !record.canSubmit || dirty) {
        return false;
      }
      setReviewAction("submit");
      setReviewActionError(null);
      try {
        const next = await client.submitModuleForReview({
          programmeId: record.programmeId,
          moduleId: record.module.id,
          expectedRevision: record.programmeRevision,
          moduleContentHash: record.moduleContentHash,
          warningsAcknowledged: options.warningsAcknowledged,
          submissionNote: options.note.trim() ? options.note.trim() : undefined,
          editLeaseId: editLeaseRef.current?.id,
        });
        const lease = editLeaseRef.current;
        releaseLease();
        if (lease) {
          // The submission has already landed, so a lease that refuses to
          // release is not a failed submit and must not be reported as one.
          await client.releaseEditLease({ leaseId: lease.id, clientId }).catch(() => undefined);
        }
        await applyReviewRecord(next);
        return true;
      } catch (cause) {
        setReviewActionError(asError(cause, "Unable to submit this module for review"));
        // Any module save bumps the programme revision the submission is
        // checked against, so the record can be stale through no fault of this
        // module. Refresh it so the retry carries the current revision.
        try {
          adoptRecord(await client.getModuleWorkspace(programmeId, moduleId, changeCaseId));
        } catch {
          // The submit error stays on screen; reload remains available.
        }
        return false;
      } finally {
        setReviewAction(null);
      }
    },
    [
      adoptRecord,
      applyReviewRecord,
      changeCaseId,
      editLeaseRef,
      releaseLease,
      client,
      clientId,
      dirty,
      moduleId,
      programmeId,
      record,
    ],
  );

  const reviewModule = useCallback(
    async (decision: ReviewDecision): Promise<void> => {
      if (!record || !record.canReview || (decision !== "approve" && !reviewNote.trim())) {
        return;
      }
      setReviewAction(decision);
      setReviewActionError(null);
      try {
        const next = await client.reviewModule({
          programmeId: record.programmeId,
          moduleId: record.module.id,
          decision,
          note: reviewNote.trim() || undefined,
        });
        setReviewNote("");
        await applyReviewRecord(next);
      } catch (cause) {
        setReviewActionError(asError(cause, "Unable to record this module review"));
      } finally {
        setReviewAction(null);
      }
    },
    [applyReviewRecord, client, record, reviewNote],
  );

  const { assessmentLedger, assessmentWeight } = useAssessmentLedger(programme, moduleDraft);

  const edits = moduleDraftEdits(updateDraft, editMappings);

  const togglePloMapping = (ploId: string, mimloId: string, checked: boolean): void => {
    editMappings((current) => {
      const ids = new Set(current[ploId] ?? []);
      if (checked) {
        ids.add(mimloId);
      } else {
        ids.delete(mimloId);
      }
      return { ...current, [ploId]: Array.from(ids) };
    });
  };

  return {
    record,
    moduleDraft,
    ploToMimlos,
    loading,
    loadError,
    retryLoad,
    saveStatus,
    saveError,
    moduleConflict,
    reviewAction,
    reviewActionError,
    reviewNote,
    leaseError,
    leaseAcquiring,
    handedBack,
    changeCaseId,
    behindStoredCopy,
    dirty,
    canEdit,
    viewOnly,
    reloading,
    assessmentWeight,
    assessmentLedger,
    ...edits,
    togglePloMapping,
    setReviewNote,
    setPloToMimlos: (update: Record<string, string[]>) => target.setPloToMimlos(update),
    setRecord: (update: (current: ModuleWorkspaceRecord | null) => ModuleWorkspaceRecord | null) =>
      target.setRecord(update),
    setSaveStatus: (status: SaveStatus) => target.setStatus(status),
    setModuleConflict: (conflict: null) => target.setConflict(conflict),
    updateDraft,
    save: () => target.flush(),
    reloadWorkspace,
    retryLease,
    submitForReview,
    reviewModule,
  };
}

export type ModuleEditorState = ReturnType<typeof useModuleEditor>;
